<template>
  <div>
    <page-header
      :title="$t('supplier_portal')"
      :subtitle="$t('supplier_portal_subtitle')"
      icon="mdi-truck-delivery"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <circle-button
          :loading="loading"
          @click="load"
          icon="mdi-refresh"
          color="primary"
          :small="false"
        />
      </template>
    </page-header>

    <page-toolbar :searchable="false">
      <template #filters>
        <v-col cols="auto">
          <date-picker v-model="filters.dateRange" color="white" />
        </v-col>

        <v-col cols="auto">
          <dropdown
            v-model="filters.status"
            :items="statusFilterItems"
            :label="$t('status')"
            item-text="name"
            item-value="value"
            hide-details
            color="white"
            style="min-width: 180px"
          />
        </v-col>

        <v-col cols="auto">
          <autocomplete
            v-model="filters.restaurantIds"
            :items="restaurantItems"
            :label="$t('restaurant')"
            item-text="name"
            item-value="id"
            hide-details
            multiple
            small-chips
            color="white"
            style="min-width: 200px"
          />
        </v-col>

        <v-col cols="auto">
          <circle-button
            :loading="loading"
            @click="load"
            icon="mdi-refresh"
            color="primary"
            :tooltip="$t('refresh')"
          />
        </v-col>
      </template>
    </page-toolbar>

    <div class="px-4 mt-3">
      <v-tabs v-model="tab" background-color="primary" dark>
        <v-tab>
          <v-icon left>mdi-file-question-outline</v-icon>
          {{ $t("warehouse.request_quote") }}
        </v-tab>
        <v-tab>
          <v-icon left>mdi-cart-outline</v-icon>
          {{ $t("warehouse.purchase_order") }}
        </v-tab>
        <v-tab>
          <v-icon left>mdi-file-document-multiple-outline</v-icon>
          {{ $t("warehouse.offers_invoices") }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" class="mt-3 pa-1">
        <v-tab-item>
          <supplier-portal-request-quote-tab
            :documents="filteredRequestDocuments"
            :restaurants="restaurantItems"
            :loading="loading"
            :date-range="filters.dateRange"
            @open-detail="openDetail"
          />
        </v-tab-item>

        <v-tab-item>
          <supplier-portal-purchase-order-tab
            :documents="filteredOrderDocuments"
            :restaurants="restaurantItems"
            :loading="loading"
            :date-range="filters.dateRange"
            @open-detail="openDetail"
          />
        </v-tab-item>

        <v-tab-item>
          <supplier-portal-offers-invoices-tab
            :date-range="filters.dateRange"
            :loading="loading"
          />
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- Detail Dialog -->
    <supplier-portal-detail-dialog v-model="detailDialog" :doc="selectedDoc" />
  </div>
</template>

<script>
import warehouseService from "@/services/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import SupplierPortalRequestQuoteTab from "@/components/suppliers/SupplierPortalRequestQuoteTab.vue";
import SupplierPortalPurchaseOrderTab from "@/components/suppliers/SupplierPortalPurchaseOrderTab.vue";
import SupplierPortalDetailDialog from "@/components/suppliers/SupplierPortalDetailDialog.vue";
import SupplierPortalOffersInvoicesTab from "@/components/suppliers/SupplierPortalOffersInvoicesTab.vue";

export default {
  name: "SupplierPortal",
  mixins: [hasSnackbar, warehouseFiltersMixin],
  components: {
    PageHeader,
    PageToolbar,
    EmptyState,
    SupplierPortalRequestQuoteTab,
    SupplierPortalPurchaseOrderTab,
    SupplierPortalDetailDialog,
    SupplierPortalOffersInvoicesTab,
  },

  data() {
    return {
      tab: 0,
      loading: false,
      documents: [],
      detailDialog: false,
      selectedDoc: null,
      filters: {
        dateRange: (() => {
          const now = new Date();
          const y = now.getFullYear();
          const m = String(now.getMonth() + 1).padStart(2, "0");
          const last = new Date(y, now.getMonth() + 1, 0).getDate();
          return [`${y}-${m}-01`, `${y}-${m}-${last}`];
        })(),
        status: null,
        restaurantIds: [],
      },
    };
  },
  mounted() {
    this.load();
  },
  watch: {
    restaurantItems: {
      immediate: true,
      handler(val) {
        this.filters.restaurantIds = val.map((r) => r.id);
      },
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await warehouseService.getSupplierDocuments();
        this.documents = res.data?.data ?? [];
      } catch (e) {
        this.showErrorSnackbar(this.$t("error"));
      } finally {
        this.loading = false;
      }
    },
    openDetail(item) {
      this.selectedDoc = item;
      this.detailDialog = true;
    },
  },
  computed: {
    filteredDocuments() {
      return this.documents.filter((doc) => {
        if (
          this.filters.dateRange &&
          this.filters.dateRange.length === 2 &&
          this.filters.dateRange[0] &&
          this.filters.dateRange[1]
        ) {
          const [from, to] = [...this.filters.dateRange].sort();
          const docDate = (doc.document_date || "").substring(0, 10);
          if (docDate < from || docDate > to) return false;
        }
        if (this.filters.status && doc.status !== this.filters.status) return false;
        if (
          this.filters.restaurantIds.length > 0 &&
          this.filters.restaurantIds.length < this.restaurantItems.length
        ) {
          const docRestId = doc.restaurant_id || doc.restaurant?.id;
          if (!this.filters.restaurantIds.includes(docRestId)) return false;
        }
        return true;
      });
    },
    filteredRequestDocuments() {
      return this.filteredDocuments.filter((d) => d.document_type === "request");
    },
    filteredOrderDocuments() {
      return this.filteredDocuments.filter((d) => d.document_type === "order");
    },
    restaurantItems() {
      return (this.$store.state.user.user?.restaurants ?? []).map((r) => ({
        ...r,
        name: this.toLatin(r.name),
      }));
    },
    statusFilterItems() {
      return [
        { name: this.$t("all"), value: null },
        ...Object.entries(this.statusKeyMap).map(([value, key]) => ({
          name: this.$t(key),
          value,
        })),
      ];
    },
  },
};
</script>
